import styled from '@emotion/styled';
import { Banner } from '../base/banner/banner';
import { BaseButton } from '../base/button/button';
import { UnderlineTabs } from '../base/underline-tabs/underline-tabs';
import { Variables } from '../base/variables';
import { SidePanel } from '../messaging/side-panel';

const V = Variables.ThemeLight;

/**
 * The design's standard button: 36px high, 6px radius, 14px type, 14px side padding. BaseButton's own default is
 * 40px, 16px type and a 3px radius, and it has no size in between (its `compact` is the design's 28px one). Only
 * the box is overridden here: colours, hover and disabled states still come from the variant.
 */
export const Button = styled(BaseButton)`
    height: 36px;
    min-width: 0;
    padding: 0 14px;
    font-size: 14px;
    border-radius: ${Variables.Rounded.medium};
`;

export type TagTone = 'success' | 'danger' | 'attention' | 'primary' | 'neutral';

/** Pale surface with a dark foreground from the same family — never a Mild/Medium pair (NS-2). */
const TAGS: Record<TagTone, { bg: string; fg: string; bd: string }> = {
    success: { bg: V.SuccessMild3, fg: V.SuccessInverted3, bd: V.SuccessMild1 },
    danger: { bg: V.WarningMild3, fg: V.WarningInverted3, bd: V.WarningMild1 },
    attention: { bg: V.AttentionMild3, fg: V.AttentionInverted3, bd: V.AttentionMild1 },
    primary: { bg: V.PrimaryMild2, fg: V.PrimaryInverted3, bd: V.PrimaryMild1 },
    neutral: { bg: V.Background1, fg: V.TextMedium, bd: V.Border1 },
};

export const Wrapper = styled.section`
    padding: 52px 56px;
    max-width: 1400px;
`;

export const PageBanner = styled(Banner)`
    margin-bottom: 16px;
`;

export const Header = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 20px;
`;

export const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

export const Title = styled.h1`
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.2;
    color: ${V.TextStrong};
`;

export const MetaRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Mono = styled.span`
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    overflow-wrap: anywhere;
`;

export const HeaderActions = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
`;

export const DirtyLabel = styled.span`
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    color: ${V.AttentionInverted3};
`;

export const ButtonContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

export const Tabs = styled(UnderlineTabs)`
    margin-bottom: 16px;
` as typeof UnderlineTabs;

export const Tag = styled.span<{ tone: TagTone }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border-radius: 4px;
    border: 1px solid ${({ tone }) => TAGS[tone].bd};
    background: ${({ tone }) => TAGS[tone].bg};
    color: ${({ tone }) => TAGS[tone].fg};
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const TwoUp = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
`;

export const Card = styled.section`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
    background: ${V.Background3};
    border: 1px solid ${V.Border1};
    border-radius: 8px;
`;

export const CardHead = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const CardTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const CardSub = styled.div`
    margin-top: 2px;
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Grow = styled.div`
    flex: 1 1 auto;
    min-width: 0;
`;

export const Facts = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    background: ${V.Border1};
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    overflow: hidden;
`;

export const Fact = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    background: ${V.Background2};
`;

export const FactLabel = styled.span`
    font-size: 11px;
    font-weight: 500;
    color: ${V.TextMild};
`;

export const FactValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const FormGrid = styled.div<{ cols?: number }>`
    display: grid;
    grid-template-columns: repeat(${({ cols }) => cols ?? 2}, minmax(0, 1fr));
    gap: 12px;
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const Label = styled.label`
    font-size: 12px;
    font-weight: 500;
    color: ${V.TextMedium};
`;

export const Hint = styled.div`
    font-size: 12px;
    line-height: 1.4;
    color: ${V.TextMild};
`;

export const ErrorText = styled.div`
    font-size: 12px;
    line-height: 1.4;
    color: ${V.WarningInverted3};
`;

export const Table = styled.section`
    background: ${V.Background3};
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    overflow: hidden;
`;

export const TableHead = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid ${V.Border1};
`;

export const Scroll = styled.div`
    overflow-x: auto;
`;

/** One grid for header and rows so the columns line up. `min` keeps wide tables scrolling instead of crushing. */
export const Row = styled.div<{ columns: string; min: number; header?: boolean; clickable?: boolean }>`
    display: grid;
    grid-template-columns: ${({ columns }) => columns};
    min-width: ${({ min }) => min}px;
    gap: 12px;
    align-items: ${({ header }) => (header ? 'center' : 'start')};
    padding: ${({ header }) => (header ? '8px 16px' : '12px 16px')};
    border-bottom: 1px solid ${V.Border1};
    background: ${({ header }) => (header ? V.Background2 : 'transparent')};
    font-size: ${({ header }) => (header ? '12px' : '13px')};
    font-weight: ${({ header }) => (header ? 500 : 400)};
    color: ${({ header }) => (header ? V.TextMild : V.TextMedium)};
    cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

    &:hover {
        background: ${({ header, clickable }) => (!header && clickable ? V.Background1 : undefined)};
    }
`;

export const Strong = styled.span`
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const Muted = styled.span`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    overflow-wrap: anywhere;
`;

export const Empty = styled.div`
    padding: 24px 16px;
    font-size: 14px;
    color: ${V.TextMild};
`;

export const Dot = styled.span<{ tone: TagTone }>`
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ tone }) => TAGS[tone].fg};
`;

export const Visibility = styled.span<{ tone: TagTone }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${({ tone }) => (tone === 'neutral' ? V.TextMild : TAGS[tone].fg)};
`;

export const Centered = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 24px;
    text-align: center;
    background: ${V.Background3};
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    color: ${V.TextMild};
    font-size: 14px;
`;

export const CenteredTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const InlineRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

/* ---- drawer (same frame as the Roster drawer) ---- */

export const Drawer = styled(SidePanel)`
    z-index: 1400;
`;

export const DrawerForm = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${V.Background2};
`;

export const DrawerHead = styled.header`
    flex: none;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 20px 14px;
    border-bottom: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const DrawerTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: ${V.TextStrong};
`;

export const DrawerBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
`;

export const DrawerFooter = styled.footer`
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SectionTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const ToggleCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid ${V.Border1};
    border-radius: 8px;
    background: ${V.Background3};
`;

/** One choice of a radio group: the design system's radio with its explanation underneath. */
export const RadioOption = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px 0;
`;

export const PickList = styled.div`
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    overflow: hidden;
    background: ${V.Background3};
`;

export const PickRow = styled.div<{ on?: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid ${V.Border1};
    background: ${({ on }) => (on ? V.Background2 : 'transparent')};

    &:last-child {
        border-bottom: none;
    }
`;

/* ---- testing ---- */

export const TestLayout = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
`;

export const RequestCard = styled(Card)`
    flex: 1 1 320px;
    max-width: 400px;
`;

export const ConsoleCard = styled.section`
    flex: 999 1 480px;
    min-width: 0;
    min-height: 560px;
    display: flex;
    flex-direction: column;
    background: ${V.Background3};
    border: 1px solid ${V.Border1};
    border-radius: 8px;
`;

export const ConsoleBar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid ${V.Border1};
`;

export const Method = styled.span`
    padding: 4px 6px;
    border-radius: 4px;
    background: ${V.Background1};
    color: ${V.TextStrong};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    font-weight: 600;
`;

export const Messages = styled.div`
    flex: 1;
    min-height: 240px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: ${V.Background2};
`;

export const Bubble = styled.div<{ mine: boolean }>`
    align-self: ${({ mine }) => (mine ? 'flex-end' : 'flex-start')};
    max-width: 85%;
    display: flex;
    flex-direction: column;
    gap: 4px;

    > span {
        align-self: ${({ mine }) => (mine ? 'flex-end' : 'flex-start')};
        font-size: 11px;
        color: ${V.TextMild};
    }

    > div {
        padding: 10px 12px;
        border-radius: 8px;
        border: 1px solid ${({ mine }) => (mine ? V.PrimaryMild1 : V.Border1)};
        background: ${({ mine }) => (mine ? V.PrimaryMild2 : V.Background3)};
        color: ${V.TextStrong};
        font-size: 14px;
        line-height: 1.45;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }
`;

export const Pre = styled.pre`
    margin: 0;
    padding: 10px;
    max-height: 180px;
    overflow: auto;
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    background: ${V.Background2};
    color: ${V.TextMedium};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

export const ResultBox = styled.div`
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};
    color: ${V.TextStrong};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

export const ResponseBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 8px 16px;
    border-top: 1px solid ${V.Border1};
    font-size: 12px;
    color: ${V.TextMedium};
`;

export const Composer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid ${V.Border1};

    /* The input (or the "lists models" hint) takes the free space. First CHILD, not first-of-type: the latter also
       matches the first button, which then grew to half the row. */
    > :first-child {
        flex: 1 1 auto;
        min-width: 0;
    }

    /* The design's Send button is sized by its content. */
    > button {
        flex: none;
    }
`;

export const SecretBox = styled.code`
    flex: 1;
    min-width: 0;
    padding: 9px 10px;
    border: 1px solid ${V.Border1};
    border-radius: 6px;
    background: ${V.Background2};
    color: ${V.TextStrong};
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    overflow-wrap: anywhere;
`;

export const KV = styled.div`
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 13px;

    > :nth-of-type(odd) {
        color: ${V.TextMild};
    }

    > :nth-of-type(even) {
        color: ${V.TextStrong};
        overflow-wrap: anywhere;
    }
`;

/**
 * BaseDialog zeroes the content padding, so the body sets its own. 24px matches the dialog title's side padding
 * (MUI DialogTitle), which keeps the title, the fields and the buttons on one left edge.
 */
export const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-sizing: border-box;
    width: 100%;
    min-width: min(480px, calc(100vw - 32px));
    padding: 0 24px 16px;
`;

/** Inside MUI DialogActions' own 8px, so the button row lines up with the 24px of the body above it. */
export const DialogActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
    padding: 0 16px 8px;
    box-sizing: border-box;
`;
